// client/src/pages/AdminUserList.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Select, MenuItem,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, FormControl, InputLabel,
} from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlineOutlined';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const roleColor = { admin: 'error', seller: 'primary', customer: 'default' };

const AdminUserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', role: 'customer' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch users. You may not have permission.');
      setUsers(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) fetchUsers(); }, [token, fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: newRole }),
      });
      if (!response.ok) throw new Error('Failed to update role.');
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
    } catch (err) {
      setError(err.message);
    }
  };

  const openForm = () => {
    setFormData({ email: '', password: '', role: 'customer' });
    setFormError('');
    setFormOpen(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create user.');
      setFormOpen(false);
      fetchUsers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>User Management</Typography>
        <Button variant="contained" onClick={openForm}>New User</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {users.length} registered {users.length === 1 ? 'user' : 'users'} for this store
      </Typography>

      {users.length === 0 ? (
        <EmptyState icon={<PeopleOutlineIcon />} title="No users yet" description="Registered users will appear here." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Joined On</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Select
                      size="small" value={user.role} onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      renderValue={(val) => <Chip label={val} size="small" color={roleColor[val] || 'default'} sx={{ textTransform: 'capitalize' }} />}
                    >
                      <MenuItem value="customer">Customer</MenuItem>
                      <MenuItem value="seller">Seller</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleCreateUser}>
          <DialogTitle>New User</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                label="Email" type="email" required fullWidth autoFocus
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <TextField
                label="Password" type="password" required fullWidth
                helperText="Share this with them directly - there's no invite email yet."
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel id="new-user-role-label">Role</InputLabel>
                <Select
                  labelId="new-user-role-label" label="Role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="seller">Seller</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>Create User</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminUserList;
